"use client";

import { IVendorUserList, IVendorUserListResponse } from "gql/CorporateValues/queries";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { getInitials } from "utils/getInitials";
import { Input, type FieldVariant } from "components/ui/v2";

interface ISearchUserProps {
  readonly selectedUser: IVendorUserList | null;
  readonly setSelectedUser: (user: IVendorUserList | null) => void;
  readonly loading: boolean;
  readonly error: any;
  readonly data: IVendorUserListResponse | undefined;
  readonly variant?: "default" | "small";
  readonly inputVariant?: FieldVariant;
}

export default function SearchUser(props: ISearchUserProps) {
  const {
    setSelectedUser,
    loading,
    error,
    data,
    variant = "default",
    inputVariant = "slate",
  } = props;
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [allUsers, setAllUsers] = useState<IVendorUserList[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IVendorUserList[]>([]);

  useEffect(() => {
    if (data?.vendorUsersListWithoutUserLogin) {
      setAllUsers(
        data.vendorUsersListWithoutUserLogin.map((u) => ({
          ...u,
          fullName: `${u.name || ""} ${u.lastname || ""}`.trim(),
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredUsers([]);
      setSelectedUser(null);
      return;
    }
    const q = inputValue.toLowerCase().trim();
    setFilteredUsers(
      allUsers.filter((u) => {
        const full = `${u.name || ""} ${u.lastname || ""}`.toLowerCase();
        return (
          full.includes(q) ||
          (u.name || "").toLowerCase().includes(q) ||
          (u.lastname || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
        );
      })
    );
  }, [inputValue, allUsers, setSelectedUser]);

  const handleSelect = (u: IVendorUserList) => {
    const fullName = `${u.name} ${u.lastname ?? ""}`.trim();
    setSelectedUser({ ...u, fullName });
    setInputValue(fullName);
    setIsOpen(false);
  };

  const hasAvatar = (u: IVendorUserList) =>
    u.profileImage &&
    u.profileImage !== "some-default-url-avatar-for-users" &&
    u.profileImage !== "";

  const usersToShow =
    inputValue.trim().length >= 1
      ? filteredUsers
      : variant === "default"
        ? allUsers
        : [];
  const showDropdown =
    isOpen &&
    (usersToShow.length > 0 ||
      (inputValue.trim().length > 0 && filteredUsers.length === 0));

  const clearButton = inputValue ? (
    <button
      type="button"
      aria-label={t("Limpiar")}
      onMouseDown={(e) => {
        e.preventDefault();
        setInputValue("");
        setSelectedUser(null);
        setIsOpen(false);
      }}
      className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-200/80 text-slate-400 transition-colors hover:bg-slate-300/80 dark:bg-zinc-700 dark:text-zinc-400"
    >
      <svg width="7" height="7" viewBox="0 0 8 8" fill="none" aria-hidden>
        <path
          d="M1 1l6 6M7 1L1 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </button>
  ) : null;

  if (loading)
    return (
      <div className="flex items-center gap-2 py-1">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-amber-500" />
        <span className="text-[12px] text-slate-400">{t("Cargando")}</span>
      </div>
    );

  if (error)
    return (
      <p className="text-[12px] text-red-400">
        {t("Error")} · {error?.message}
      </p>
    );

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        size="sm"
        variant={inputVariant}
        label={t("benefit.Buscar por nombre o email")}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        rightIcon={clearButton}
      />

      {showDropdown && (
        <div className="absolute top-full z-[12050] mt-2 max-h-60 w-full overflow-auto overscroll-contain rounded-2xl border border-slate-200 bg-white shadow-[0_22px_70px_-18px_rgba(15,23,42,0.45),0_10px_24px_-16px_rgba(79,70,229,0.35),inset_0_1px_0_rgba(255,255,255,0.9)] ring-1 ring-black/5 dark:border-zinc-700 dark:bg-zinc-950 dark:shadow-[0_22px_70px_-18px_rgba(0,0,0,0.75),0_10px_24px_-16px_rgba(99,102,241,0.45)] dark:ring-white/10">
          {usersToShow.length === 0 && inputValue.trim().length > 0 ? (
            <p className="px-3.5 py-3 text-[12px] text-slate-400 dark:text-zinc-500">
              {t("Sin resultados para")} «{inputValue}»
            </p>
          ) : (
            usersToShow.map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={() => handleSelect(u)}
                className="flex w-full items-center gap-2.5 border-b border-slate-100 px-3 py-2.5 text-left transition-colors last:border-b-0 first:rounded-t-2xl last:rounded-b-2xl hover:bg-indigo-50 dark:border-zinc-800 dark:hover:bg-indigo-500/10"
              >
                <div className="relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-xl">
                  {hasAvatar(u) ? (
                    <Image
                      src={u.profileImage!}
                      alt="avatar"
                      fill
                      className="object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center rounded-xl bg-amber-100 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {getInitials(`${u.name} ${u.lastname}`)}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium capitalize text-slate-700 dark:text-zinc-200">
                    {`${u.name?.toLowerCase() || ""} ${u.lastname?.toLowerCase() || ""}`.trim()}
                  </p>
                  <p className="truncate text-[11px] text-slate-400 dark:text-zinc-500">
                    {u.email}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
